// Replace the entire MilitaryAdminQuestionsView.jsx with this fixed version:

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import config from '../../../config';
import { toast } from '../../Common/Toast';
import './MilitaryAdminQuestionsView.css';

const MilitaryAdminQuestionsView = ({ sessionId, token, weekNumber, candidateName, onClose }) => {
  // console.log("🎬 MilitaryAdminQuestionsView rendering with sessionId:", sessionId);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  const socketRef = useRef(null);
  const sessionIdRef = useRef(sessionId);

  // Update ref when sessionId changes
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // Fetch questions from API
  const fetchSessionQuestions = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    
    try {
      // console.log("📡 Fetching military session questions from API...");
      const response = await axios.get(
        `${config.baseURL}/api/military/sessions/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        // console.log("✅ API data received:", response.data.session);
        const sessionData = response.data.session;
        
        // FIX: Sort questions by ID to ensure consistent order
        const sortedQuestions = [...sessionData.questions].sort((a, b) => a.id - b.id);
        
        setQuestions(sortedQuestions);
        setTotalQuestions(sessionData.total_questions);
        setAnsweredCount(sessionData.answered_count);
        
        // FIX: Find current question (first unanswered in sorted order)
        const unanswered = sortedQuestions.find(q => !q.answered);
        if (unanswered) {
          setCurrentQuestionId(unanswered.id);
          // console.log("🎯 Current question ID set to:", unanswered.id);
        } else if (sortedQuestions.length > 0) {
          // All questions answered, set to last question
          setCurrentQuestionId(sortedQuestions[sortedQuestions.length - 1].id);
        }
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      console.error("Error fetching questions:", err);
      if (!silent) setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [sessionId, token]);

  // Initialize socket connection for real-time updates
  useEffect(() => {
    if (!sessionId || !token) return;
    
    const sessionIdStr = String(sessionId);
    console.log("🔌 Initializing socket connection for military session:", sessionIdStr);
    
    socketRef.current = io(config.baseURL, {
      auth: { token },
      transports: ["websocket", "polling"],
    });
    
    socketRef.current.on("connect", () => {
      console.log("✅ Socket connected, joining military session room:", sessionIdStr);
      socketRef.current.emit("join-military-room", {
        sessionId: sessionIdStr,
        userType: "admin"
      });
    });
    
    // FIX: Listen for question answered events
    socketRef.current.on("military-question-answered", (eventData) => {
      console.log("📡 MILITARY-QUESTION-ANSWERED event received:", eventData);
      
      // Check if this event is for our session
      if (String(eventData.sessionId) === sessionIdRef.current) {
        console.log(`✅ Processing answer for question ${eventData.questionId}`);
        
        // Update answered count
        setAnsweredCount(eventData.questionNumber);
        
        // FIX: Update questions state to mark this question as answered
        setQuestions(prevQuestions => {
          const updatedQuestions = prevQuestions.map(q => 
            q.id === eventData.questionId ? { ...q, answered: true } : q
          );
          
          // Find the next unanswered question
          const nextUnanswered = updatedQuestions.find(q => !q.answered);
          if (nextUnanswered) {
            setCurrentQuestionId(nextUnanswered.id);
            console.log("🎯 Next current question ID:", nextUnanswered.id);
          } else {
            console.log("📋 All questions answered!");
          }
          
          return updatedQuestions;
        });
        
        // Also refresh from server to get answer text
        setTimeout(() => {
          fetchSessionQuestions(true);
        }, 500);
      }
    });
    
    // FIX: Listen for answer updates (includes answer text)
    socketRef.current.on("military-answer-update", (eventData) => {
      console.log("📡 MILITARY-ANSWER-UPDATE event received:", eventData);
      
      if (String(eventData.sessionId) === sessionIdRef.current) {
        // Refresh questions to get the answer text
        fetchSessionQuestions(true);
      }
    });
    
    return () => {
      if (socketRef.current) {
        socketRef.current.emit("leave-military-room", { sessionId: sessionIdStr });
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [sessionId, token, fetchSessionQuestions]);

  // Initial fetch
  useEffect(() => {
    fetchSessionQuestions(false);
  }, [fetchSessionQuestions]);

  const toggleQuestion = (questionId) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const getStatusBadge = (answered, questionId) => {
    const isCurrent = currentQuestionId === questionId && !answered;
    
    if (isCurrent) {
      return <span className="military_status-badge current">▶ Current Question</span>;
    }
    if (answered) {
      return <span className="military_status-badge answered">✓ Answered</span>;
    }
    return <span className="military_status-badge pending">⏳ Pending</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const filterQuestions = (questionsList) => {
    if (!questionsList) return [];
    return questionsList.filter(q => {
      const matchesSearch = searchTerm === '' || 
        q.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (q.category && q.category.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesStatus = filterStatus === 'all' || 
        (filterStatus === 'answered' && q.answered) ||
        (filterStatus === 'pending' && !q.answered);
      return matchesSearch && matchesStatus;
    });
  };

  const completionPercentage = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  if (loading) {
    return (
      <div className="military_admin-questions-overlay">
        <div className="military_admin-questions-modal">
          <div className="military_loading-container">
            <div className="military_spinner"></div>
            <p>Loading session questions...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="military_admin-questions-overlay">
        <div className="military_admin-questions-modal">
          <div className="military_error-container">
            <h3>Error</h3>
            <p>{error}</p>
            <button onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  const filteredQuestions = filterQuestions(questions);

  return (
    <div className="military_admin-questions-overlay">
      <div className="military_admin-questions-modal">
        <div className="military_modal-header">
          <h2>Week {weekNumber} Assessment - {candidateName || 'Candidate'}</h2>
          <button className="military_close-btn" onClick={onClose}>&times;</button>
        </div>

        {/* Progress Summary */}
        <div className="military_progress-summary">
          <div className="military_progress-stats">
            <div className="military_stat-card">
              <span className="military_stat-label">Total</span>
              <span className="military_stat-value">{totalQuestions}</span>
            </div>
            <div className="military_stat-card">
              <span className="military_stat-label">Answered</span>
              <span className="military_stat-value answered">{answeredCount}</span>
            </div>
            <div className="military_stat-card">
              <span className="military_stat-label">Pending</span>
              <span className="military_stat-value pending">{totalQuestions - answeredCount}</span>
            </div>
            <div className="military_stat-card">
              <span className="military_stat-label">Progress</span>
              <span className="military_stat-value">{completionPercentage}%</span>
            </div>
          </div>
          <div className="military_progress-bar-container">
            <div 
              className="military_progress-bar-fill" 
              style={{ 
                width: `${completionPercentage}%`,
                transition: 'width 0.3s ease-in-out'
              }} 
            />
          </div>
        </div>

        {/* Search and Filter */}
        <div className="military_search-filter-bar">
          <input
            type="text"
            placeholder="Search questions or categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="military_search-input"
          />
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Questions</option>
            <option value="answered">Answered Only</option>
            <option value="pending">Pending Only</option>
          </select>
        </div>

        {/* Questions List */}
        <div className="military_questions-container">
          {filteredQuestions.length === 0 ? (
            <div className="military_no-results">
              <p>No questions match your search criteria</p>
            </div>
          ) : (
            filteredQuestions.map((question, idx) => {
              const isCurrent = currentQuestionId === question.id && !question.answered;
              const isExpanded = expandedQuestions[question.id];
              
              return (
                <div 
                  key={question.id}
                  id={`question-${question.id}`}
                  className={`military_question-card ${isCurrent ? 'current' : ''} ${question.answered ? 'answered' : ''}`}
                >
                  <div className="military_question-card-header" onClick={() => toggleQuestion(question.id)}>
                    <div className="military_question-info">
                      <span className="military_question-number">Q{idx + 1}</span>
                      <span className="military_question-preview">
                        {question.text.length > 80 ? question.text.substring(0, 80) + '...' : question.text}
                      </span>
                      {getStatusBadge(question.answered, question.id)}
                    </div>
                    <span className="military_expand-icon">{isExpanded ? '▲' : '▼'}</span>
                  </div>
                  
                  {isExpanded && (
                    <div className="military_question-details">
                      <div className="military_question-full-text">
                        <strong>Question:</strong>
                        <p>{question.text}</p>
                      </div>
                      
                      <div className="military_question-meta">
                        <span><strong>Category:</strong> {question.category || 'General'}</span>
                        {question.answered_at && (
                          <span><strong>Answered:</strong> {formatDate(question.answered_at)}</span>
                        )}
                      </div>
                      
                      <div className="military_answer-section">
                        <strong>Response:</strong>
                        {!question.answered ? (
                          <p className="military_waiting-text">Waiting for candidate's response...</p>
                        ) : question.answer_text ? (
                          <div className="military_answer-content">
                            {question.answer_text}
                          </div>
                        ) : (
                          <p className="military_waiting-text">✓ Answered (response details loading...)</p>
                        )}
                        
                        {question.reaction_time && (
                          <div className="military_reaction-time">
                            <strong>Response Time:</strong> {question.reaction_time} seconds
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default MilitaryAdminQuestionsView;